/**
 * 钢材买料价格预测模型（江山对账单口径）
 *
 * 本文件是「价格预测」的唯一数据源与唯一算法实现，被两处共用，保证结果完全一致：
 *   ① src/views/CalculatorDialog.vue   —— /cal 计算器「价格预测」面板
 *   ② src/views/LaserOrderDialog.vue   —— 报价明细表 Th 列的每行预测吨价
 *
 * 预测分两层（2026-09-22 起新增第二层）：
 *   ① 精确层：同材质 + 同厚度 的历史成交，按「面积接近度 × 时间新度」加权平均
 *              —— 不同材质、不同厚度一律不参与，不做插值/外推
 *   ② 通用层：该「材质 + 厚度」无成交记录时，退回历史成交记录，
 *              搬用「同材质内成交记录最多的那个厚度档」的加权吨价作为通用预测值
 *              —— 仍在同材质内取数（不跨材质），但确实跨了厚度，故必须明确标注搬自哪一档，
 *                 且只作量级参考，不能当作该厚度的真实成交价
 *   ③ 该材质在对账单中完全无记录（不锈钢 / 铝板）→ 无法预测
 *
 * 另提供 historyRowsFor()：「历史成交记录 · 预测依据」面板专用取数。
 *   填了厚度 → 与预测完全同源（精确层 / 通用层的同一批记录）；
 *   没填厚度 → 不按厚度过滤，列出该材质（或全部材质）的历史成交，供逐条浏览核对。
 *
 * 数据范围：江山对账单 2026-04 ~ 2026-08；新增月份只需往 PRICE_HISTORY 追加记录，
 *          并在 HISTORY_SOURCE_FILES 追加文件名——所有条数 / 面积 / 重量 / 金额 /
 *          材质说明里的覆盖厚度都会自动重算，不需要手工改数字。
 */

// ==================== 模型配置 ====================
export const PRICE_MODEL = {
  // ===== ① 精确层：同材质 + 同厚度 的历史成交加权 =====
  // 预测吨价 = 该材质该厚度的成交记录，按「面积接近度 × 时间新度」加权平均
  //   w = 1 / (|单件面积差| + areaSmooth) × timeFactor
  // 严格只取同材质 + 同厚度：不同材质、不同厚度一律不参与预测（不做跨厚度插值/外推）。
  //   因此某材质的某厚度若没有成交记录，则退回「通用层」（同材质样本最多档）。
  //   时间切分 MAPE 4.34%，逐条留一 MAPE 2.87%（中位 1.33%）
  areaSmooth: 0.20,      // 面积平滑量 ㎡
  // 薄板口径：厚度 ≤ 3mm 时行业习惯按平方（元/㎡）报价，此时主口径显示每平方价，吨价并列保留
  thinThick: 3,
  // 时间新度因子：同尺寸下，买料日期越近权重越高
  //   timeFactor = timeBoost ^ recency
  //   recency = (本条买料日 − 该材质最早买料日) ÷ (该材质最新买料日 − 最早买料日)，取值 0~1
  //   即：该材质最新一条买料记录 = timeBoost 倍权重，最早一条 = 1 倍
  timeBoost: 100,
  // 模型验证（2026-09-22 加入 8 月数据后重算，有效样本 115 条）
  //   逐条留一：逐条抽出，用「同材质 + 同厚度」的其余成交预测该条，口径与线上预测完全一致
  //   时间切分：只用「同材质 + 同厚度」中日期更早的成交去预测更晚的成交（expanding window）
  valSplitMape: 3.74,    // 时间切分验证 MAPE %
  valLooMape: 2.78,      // 逐条留一验证 MAPE %
  valLooMedian: 1.21,    // 逐条留一验证 中位误差 %
  // 离群剔除：同一 (材质,厚度) 内，吨价超出中位数的 [0.625, 1.6] 倍即视为离群（如错记、非钢种混记）
  outlierLo: 0.625,
  outlierHi: 1.6,
  // 面积档（用于把同厚度历史记录按面积分区展示，不再作为系数参与乘算）
  areaBand: [
    { label: "≥8m²（整板）", min: 8, max: 999 },
    { label: "4-8m²", min: 4, max: 8 },
    { label: "2-4m²", min: 2, max: 4 },
    { label: "1-2m²", min: 1, max: 2 },
    { label: "0.5-1m²", min: 0.5, max: 1 },
    { label: "<0.5m²", min: 0, max: 0.5 },
  ],
  // 材质说明（族 key 对齐 LaserOrderDialog：铁板(碳钢) / 锰板 / 镀锌 / 不锈钢）
  //   有成交数据的材质（铁板 / 锰板 / 镀锌）的说明在清洗完成后由数据实时生成，见文件下方
  //   「材质说明（数据驱动）」一节；此处只保留对账单里完全没有记录的材质。
  matNote: {
    '不锈钢': "对账单中无不锈钢成交记录，无历史成交价可参考",
    '铝板': "对账单中无铝板成交记录，无历史成交价可参考"
  },
  // 材质展示顺序（可预测组合总览用；无数据材质固定排最后并标注）
  matOrder: ['铁板', '锰板', '镀锌', '不锈钢'],
  // 市场对标（我的钢铁网 Mysteel）
  market: [
    { name: "南宁 热轧板卷 4.75×1500 柳钢", price: 3360, date: "2026-09-01", scope: "南宁市场", src: "我的钢铁网 Mysteel" },
    { name: "全国 24 城 4.75mm 热卷均价", price: 3368, date: "2026-09-09", scope: "全国", src: "我的钢铁网 Mysteel" },
    { name: "南宁 中厚板 20mm 普板 柳钢", price: 3720, date: "2026-09-01", scope: "南宁市场", src: "我的钢铁网 Mysteel" },
    { name: "全国 24 城 20mm 普板均价", price: 3584, date: "2026-09-09", scope: "全国", src: "我的钢铁网 Mysteel" },
    { name: "唐山 普方坯出厂", price: 3040, date: "2026-09-09", scope: "唐山", src: "我的钢铁网 Mysteel" },
    { name: "南宁 重废（钢厂收购）", price: 2240, date: "2026-09-01", scope: "南宁市场", src: "我的钢铁网 Mysteel" },
  ],
  // 数据源汇总（去重 + 剔离群后的有效口径）
  //   条数 / 面积 / 重量 / 金额 / 吨价均值全部由清洗结果实时生成（见下方「数据源汇总」一节），
  //   避免每加一个月对账单就要手工改一遍数字。此处只保留静态说明文本。
  source: {
    scope: "对账单中标注采购的行（规格形如 长x宽x厚 的行均计入）；同材质+同厚度+同尺寸只保留最新一条；面积按理论尺寸，重量按密度 7.85 t/m³（不锈钢 7.93）",
    caveat: "钢材实际交货普遍有 3%~8% 负公差，若按过磅结算，真实吨价需上浮 3%~9%"
  }
}

// ==================== 买料历史明细 ====================
// [日期, 材质, 规格, 厚(mm), 单件面积(㎡), 数量, 总价(元), 每吨价(元/t)]
export const PRICE_HISTORY = [
  ["2026-04-07","锰板","1100x1700x4锰",4,1.870,2,490,4172],
  ["2026-04-07","锰板","120x900x4锰",4,0.108,2,28,4128],
  ["2026-04-07","铁板","1255x730x4",4,0.916,1,101,3511],
  ["2026-04-07","锰板","200x800x5锰",5,0.160,2,50,3981],
  ["2026-04-07","铁板","480x1425x3",3,0.684,1,65,4035],
  ["2026-04-08","铁板","2400x1510x4",4,3.624,1,396,3480],
  ["2026-04-09","铁板","1815x1010x5",5,1.833,1,266,3697],
  ["2026-04-11","铁板","1230x1250x1",1,1.538,1,139,11517],
  ["2026-04-11","铁板","1230x1510x3",3,1.857,1,166,3795],
  ["2026-04-11","铁板","510x1010x3",3,0.515,1,49,4039],
  ["2026-04-11","铁板","655x615x3",3,0.403,1,38,4006],
  ["2026-04-11","铁板","825x1260x3",3,1.040,1,94,3840],
  ["2026-04-11","铁板","935x735x2",2,0.687,1,65,6024],
  ["2026-04-13","铁板","1200x390x5",5,0.468,2,140,3811],
  ["2026-04-13","锰板","595x260x5锰",5,0.155,1,24,3953],
  ["2026-04-14","铁板","1020x765x1.2",1.2,0.780,1,43,5850],
  ["2026-04-15","锰板","370x1270x3锰",3,0.470,1,56,5060],
  ["2026-04-16","铁板","475x830x3",3,0.394,1,37,3985],
  ["2026-04-23","铁板","1510x2000x5",5,3.020,1,424,3577],
  ["2026-04-28","铁板","1510x6000x6",6,9.060,1,1438,3370],
  ["2026-04-28","铁板","1510x760x6",6,1.148,1,198,3663],
  ["2026-04-28","铁板","1700x1260x2",2,2.142,1,150,4460],
  ["2026-05-04","铁板","2145x1510x2.5",2.5,3.239,1,273,4295],
  ["2026-05-04","铁板","2345x1260x1.5",1.5,2.955,1,177,5087],
  ["2026-05-09","铁板","1510x1690x5",5,2.552,1,358,3574],
  ["2026-05-13","铁板","1510x730x2.75",2.75,1.102,1,104,4371],
  ["2026-05-13","铁板","1510x870xx1.5",1.5,1.314,1,85,5495],
  ["2026-05-14","铁板","1510x860x2.5",2.5,1.299,1,116,4552],
  ["2026-05-15","铁板","1510x335x2",2,0.506,1,35,4407],
  ["2026-05-15","铁板","375x925x2",2,0.347,1,24,4407],
  ["2026-05-16","锰板","450x1170x6锰",6,0.526,1,99,3992],
  ["2026-05-17","铁板","1070x660x1",1,0.706,1,32,5772],
  ["2026-05-17","铁板","1310x295x5",5,0.386,1,57,3758],
  ["2026-05-17","铁板","1510x410x3",3,0.619,1,55,3772],
  ["2026-05-17","铁板","335x1510x2",2,0.506,1,35,4407],
  ["2026-05-21","铁板","1510x560x4",4,0.846,1,92,3465],
  ["2026-05-23","铁板","1510x575x5",5,0.868,1,122,3580],
  ["2026-05-23","镀锌","845x240x3镀",3,0.203,1,20,4188],
  ["2026-05-24","铁板","1510x735x6",6,1.110,1,187,3577],
  ["2026-05-24","铁板","330x1015x3",3,0.335,1,36,4564],
  ["2026-05-24","铁板","445x1250x1",1,0.556,1,25,5725],
  ["2026-05-24","铁板","735x1510x2",2,1.110,1,77,4419],
  ["2026-05-27","铁板","1510x1530x6",6,2.310,1,389,3575],
  ["2026-05-30","铁板","1825x1510x4",4,2.756,1,317,3663],
  ["2026-06-01","铁板","490x490x5",5,0.240,1,35,3714],
  ["2026-06-01","铁板","775x1020x3",3,0.790,1,75,4029],
  ["2026-06-03","铁板","1510x1200x3",3,1.812,1,172,4031],
  ["2026-06-03","铁板","1510x2200x6",6,3.322,1,563,3598],
  ["2026-06-03","铁板","1510x815x4",4,1.231,1,135,3494],
  ["2026-06-03","镀锌","310x1145x2镀",2,0.355,1,29,5204],
  ["2026-06-04","铁板","1510x1015x4",4,1.533,1,168,3491],
  ["2026-06-07","铁板","390x390x3",3,0.152,1,14,3908],
  ["2026-06-10","锰板","650x1510x6锰",6,0.982,1,185,4002],
  ["2026-06-12","铁板","1510x475x3",3,0.717,1,68,4026],
  ["2026-06-13","铁板","1510x3560x4",4,5.376,1,544,3223],
  ["2026-06-13","铁板","1510x6000x6",6,9.060,1,1325,3105],
  ["2026-06-13","铁板","1610x1510x3",3,2.431,1,218,3808],
  ["2026-06-14","铁板","1510x1660x2.5",2.5,2.507,1,200,4066],
  ["2026-06-14","铁板","1510x450x5",5,0.679,1,96,3599],
  ["2026-06-15","铁板","1250x1060x1",1,1.325,1,60,5769],
  ["2026-06-15","铁板","1510x355x3",3,0.536,1,48,3802],
  ["2026-06-15","铁板","1510x760x5",5,1.148,1,162,3597],
  ["2026-06-15","铁板","2250x1510x6",6,3.397,1,540,3375],
  ["2026-06-18","铁板","2825x1260x2",2,3.559,1,231,4134],
  ["2026-06-18","铁板","605x740x2",2,0.448,1,31,4410],
  ["2026-06-20","铁板","1510x425x5",5,0.642,1,90,3573],
  ["2026-06-20","铁板","1510x630x5",5,0.951,1,134,3589],
  ["2026-06-20","铁板","805x605x2",2,0.487,1,34,4447],
  ["2026-06-22","镀锌","1220x2500x1镀",1,3.050,2,200,4177],
  ["2026-06-22","锰板","1510x915x3锰",3,1.382,1,138,4241],
  ["2026-06-23","铁板","1510x460x6",6,0.695,1,118,3607],
  ["2026-06-26","铁板","1510x1000x4",4,1.510,1,166,3501],
  ["2026-06-26","铁板","2170x1510x5",5,3.277,1,437,3398],
  ["2026-06-26","铁板","425x1510x5",5,0.642,1,90,3573],
  ["2026-06-28","铁板","1510x6000x6",6,9.060,1,1325,3105],
  ["2026-06-29","铁板","1510x465x5",5,0.702,1,98,3556],
  ["2026-06-30","锰板","1510x310x3锰",3,0.468,1,52,4717],
  ["2026-07-01","锰板","290x730x5猛",5,0.212,1,33,3971],
  ["2026-07-02","铁板","1510x815x4",4,1.231,1,135,3494],
  ["2026-07-07","铁板","2365x1510x6",6,3.571,1,600,3567],
  ["2026-07-11","铁板","1510x1060x5",5,1.601,1,226,3597],
  ["2026-07-12","铁板","1400x310x2",2,0.434,1,32,4696],
  ["2026-07-13","铁板","1510x580x4",4,0.876,1,96,3491],
  ["2026-07-14","锰板","310x610x6锰",6,0.189,1,35,3930],
  ["2026-07-15","铁板","1260x1100x1.5",1.5,1.386,1,83,5086],
  ["2026-07-16","铁板","1510x210x6",6,0.317,1,54,3616],
  ["2026-07-16","锰板","1510x615x3锰",3,0.929,1,93,4252],
  ["2026-07-16","铁板","1510x710x4",4,1.072,1,117,3476],
  ["2026-07-16","锰板","310x175x3锰",3,0.054,1,6,4696],
  ["2026-07-16","锰板","610x610x5锰",5,0.372,1,58,3971],
  ["2026-07-18","铁板","1510x805x3",3,1.216,1,109,3808],
  ["2026-07-19","铁板","1510x310x4",4,0.468,1,51,3470],
  ["2026-07-19","铁板","4120x1510x5",5,6.221,1,830,3399],
  ["2026-07-20","铁板","1510x410x2.5",2.5,0.619,1,50,4115],
  ["2026-07-25","铁板","1510x525x2",2,0.793,1,55,4419],
  ["2026-07-25","铁板","1510x800x3",3,1.208,1,109,3831],
  ["2026-07-25","镀锌","650x810x1.5镀",1.5,0.526,1,34,5484],
  ["2026-07-28","铁板","1020x760x2",2,0.775,1,54,4437],
  ["2026-07-28","铁板","1510x795x6",6,1.200,1,204,3608],
  ["2026-07-29","铁板","1510x1410x2.5",2.5,2.129,1,170,4069],
  ["2026-07-29","铁板","1510x705x5",5,1.065,1,150,3590],
  ["2026-07-30","铁板","1510x725x3",3,1.095,1,98,3801],
  ["2026-08-01","铁板","1510x1720x2.5",2.5,2.597,1,206,4042],
  ["2026-08-01","铁板","1510x275x5",5,0.415,1,58,3559],
  ["2026-08-01","镀锌","725x785x1.2镀",1.2,0.569,1,31,5782],
  ["2026-08-05","铁板","1510x450x5",5,0.679,1,95,3562],
  ["2026-08-05","铁板","4620x1510x6",6,6.976,1,1110,3378],
  ["2026-08-06","铁板","1510x160x5",5,0.242,1,34,3585],
  ["2026-08-06","铁板","2975x1510x5",5,4.492,1,595,3375],
  ["2026-08-09","铁板","320x1510x4",4,0.483,1,53,3493],
  ["2026-08-13","铁板","680x290x2花",2,0.197,1,14,4522],
  ["2026-08-14","铁板","6000x1510x4",4,9.060,1,880,3093],
  ["2026-08-15","锰板","1865x600x6锰",6,1.119,1,210,3984],
  ["2026-08-17","锰板","1830x1510x6锰",6,2.763,1,504,3872],
  ["2026-08-21","铁板","2610x210x4",4,0.548,1,60,3486],
  ["2026-08-22","铁板","1510x2200x5",5,3.322,1,466,3574],
  ["2026-08-22","铁板","4320x1510x2",2,6.523,1,420,4101],
  ["2026-08-24","铁板","1510x1560x2.5",2.5,2.356,1,187,4045],
  ["2026-08-24","锰板","2480x1510x8锰",8,3.745,1,911,3874],
  ["2026-08-28","铁板","1510x360x6",6,0.544,1,92,3593],
  ["2026-08-31","铁板","1510x1810x6",6,2.733,1,448,3480],
  ["2026-08-31","铁板","1730x1510x3.75",3.75,2.612,1,272,3537]
]

// ==================== 材质别名 ====================
// 报价单 V2 / LaserOrderDialog 的材质族 key → 对账单口径材质名
export const MATERIAL_ALIAS = {
  '铁板': '铁板', '碳钢': '铁板', 'Q235': '铁板', 'Q355': '铁板',
  '锰板': '锰板', '锰钢': '锰板',
  '镀锌': '镀锌', '镀锌板': '镀锌',
  '304': '不锈钢', '201': '不锈钢', '316': '不锈钢', '310S': '不锈钢', '不锈钢': '不锈钢',
  '铝板': '铝板', '铝': '铝板'
}

// 任意材质写法 → 对账单口径材质名（识别失败时原样返回）
export function modelMaterialOf (materialKey) {
  const key = String(materialKey == null ? '' : materialKey).trim()
  if (!key) return ''
  if (MATERIAL_ALIAS[key]) return MATERIAL_ALIAS[key]
  if (/^\d{3}[A-Za-z]?$/.test(key) || key.indexOf('不锈') >= 0) return '不锈钢'
  if (key.indexOf('锰') >= 0) return '锰板'
  if (key.indexOf('镀锌') >= 0 || key.indexOf('锌') >= 0) return '镀锌'
  if (key.indexOf('铝') >= 0) return '铝板'
  if (key.indexOf('铁') >= 0 || key.indexOf('碳钢') >= 0) return '铁板'
  return key
}

// ==================== 纯工具函数 ====================
// 日期字符串 "YYYY-MM-DD" → 天序号（用于比较与归一化）
export function dayIndexOf (dateText) {
  const parts = String(dateText || '').split('-')
  if (parts.length !== 3) return 0
  return Date.UTC(parts[0] - 0, parts[1] - 0 - 1, parts[2] - 0) / 86400000
}

// 规格字符串 → 去重键：材质 + 厚度 + 长宽（排序后，故 230x800 与 800x230 视为同一尺寸）
export function specKeyOf (item) {
  const nums = String(item[2] || '').match(/\d+(?:\.\d+)?/g)
  if (!nums || nums.length < 2) return item[1] + '|' + item[3] + '|' + item[2]
  const sizeA = nums[0] - 0
  const sizeB = nums[1] - 0
  return item[1] + '|' + item[3] + '|' + Math.min(sizeA, sizeB) + 'x' + Math.max(sizeA, sizeB)
}

// ==================== 模块加载时一次性清洗 ====================
// 同材质 + 同厚度 + 同尺寸的成交记录只保留最新一条
const DEDUP_HISTORY = (() => {
  const latest = {}
  PRICE_HISTORY.forEach(item => {
    const key = specKeyOf(item)
    const cur = latest[key]
    if (!cur || dayIndexOf(item[0]) > dayIndexOf(cur[0])) latest[key] = item
  })
  return PRICE_HISTORY.filter(item => latest[specKeyOf(item)] === item)
})()

// 被合并掉的同规格旧记录条数
export const DUP_COUNT = PRICE_HISTORY.length - DEDUP_HISTORY.length

// 各 (材质|厚度) 的吨价中位数，用于离群剔除
const TH_MEDIAN = (() => {
  const groups = {}
  DEDUP_HISTORY.forEach(item => {
    const key = item[1] + '|' + item[3]
    if (!groups[key]) groups[key] = []
    groups[key].push(item[7])
  })
  const median = {}
  Object.keys(groups).forEach(key => {
    const arr = groups[key].slice().sort((x, y) => x - y)
    const mid = arr.length >> 1
    median[key] = arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2
  })
  return median
})()

// 剔除离群后的有效成交池（同材质+同厚度内吨价超出中位数 [0.625, 1.6] 倍视为离群）
export const CLEAN_HISTORY = DEDUP_HISTORY.filter(item => {
  const med = TH_MEDIAN[item[1] + '|' + item[3]]
  if (!med) return true
  const ratio = item[7] / med
  return ratio >= PRICE_MODEL.outlierLo && ratio <= PRICE_MODEL.outlierHi
})

// 被剔除的离群条数（去重后仍未通过中位数区间检验的）
export const OUTLIER_COUNT = DEDUP_HISTORY.length - CLEAN_HISTORY.length

// 各材质的买料日期跨度（时间新度归一化的分母）
const DATE_EXTENT = (() => {
  const extent = {}
  CLEAN_HISTORY.forEach(item => {
    const day = dayIndexOf(item[0])
    const cur = extent[item[1]]
    if (!cur) extent[item[1]] = { min: day, max: day }
    else {
      if (day < cur.min) cur.min = day
      if (day > cur.max) cur.max = day
    }
  })
  return extent
})()

// 各材质覆盖的厚度档（升序）
export const THICKNESS_LIST_BY_MAT = (() => {
  const list = {}
  CLEAN_HISTORY.forEach(item => {
    if (!list[item[1]]) list[item[1]] = []
    if (list[item[1]].indexOf(item[3]) === -1) list[item[1]].push(item[3])
  })
  Object.keys(list).forEach(mat => list[mat].sort((x, y) => x - y))
  return list
})()

// 「通用预测」参考档：同材质内成交记录最多的厚度档（每个材质列出全部档，按条数降序、厚度升序）
//   真正的选取在 pickTopCombo 里做：条数最多优先；条数并列时取厚度更接近请求厚度的档（再并列取更薄的一档）。
//   这样镀锌（4 个厚度档各只有 1 条成交）这类样本稀疏材质也能得到稳定、可解释的结果。
export const COMBO_LIST_BY_MAT = (() => {
  const groups = {}
  CLEAN_HISTORY.forEach(item => {
    const key = item[1] + '|' + item[3]
    if (!groups[key]) groups[key] = { material: item[1], thickness: item[3], records: [] }
    groups[key].records.push(item)
  })
  const byMat = {}
  Object.keys(groups).forEach(key => {
    const one = groups[key]
    if (!byMat[one.material]) byMat[one.material] = []
    byMat[one.material].push(one)
  })
  Object.keys(byMat).forEach(mat => {
    byMat[mat].sort((x, y) => (y.records.length - x.records.length) || (x.thickness - y.thickness))
  })
  return byMat
})()

// 取某材质的通用预测参考档（同材质内成交记录最多的档）
export function pickTopCombo (material, thickness) {
  const list = COMBO_LIST_BY_MAT[material]
  if (!list || !list.length) return null
  const target = Number(thickness) || 0
  let best = list[0]
  list.forEach(one => {
    if (one.records.length > best.records.length) { best = one; return }
    if (one.records.length < best.records.length) return
    // 条数并列：取厚度更接近请求厚度的档，再并列取更薄的一档
    const diffOne = Math.abs(one.thickness - target)
    const diffBest = Math.abs(best.thickness - target)
    if (diffOne < diffBest || (diffOne === diffBest && one.thickness < best.thickness)) best = one
  })
  return best
}

// 兼容旧名（= 样本最多的档，无请求厚度时按条数取）
export const TOP_COMBO_BY_MAT = (() => {
  const top = {}
  Object.keys(COMBO_LIST_BY_MAT).forEach(mat => { top[mat] = COMBO_LIST_BY_MAT[mat][0] })
  return top
})()

// 供 /cal 「可预测组合总览」使用的材质统计（条数 / 覆盖厚度）
export const MATERIAL_HISTORY_STAT = Object.keys(THICKNESS_LIST_BY_MAT).map(mat => ({
  material: mat,
  count: CLEAN_HISTORY.filter(item => item[1] === mat).length,
  thicknessList: THICKNESS_LIST_BY_MAT[mat]
}))

// ==================== 材质说明（数据驱动） ====================
// 有成交数据的材质：条数与厚度覆盖由清洗结果实时生成，避免每加一个月对账单就要手工改数字。
//   尾注里的溢价是 2026-09-22 用 115 条有效样本实测的中位吨价对比（同厚度比较）：
//   锰板比铁板高 10%~20%（5/6mm 约 +11%，3/4mm 因锰板多为小件而拉到 +19%~20%）。
const MAT_NOTE_TAIL = {
  '铁板': '，样本最充分',
  '锰板': '，同厚度吨价比铁板高约 10%~20%',
  '镀锌': '，厚度档稀疏，结果可靠性偏低'
}
MATERIAL_HISTORY_STAT.forEach(one => {
  const ths = one.thicknessList.map(t => t + ' mm')
  PRICE_MODEL.matNote[one.material] = '对账单 ' + one.count + ' 条成交，覆盖 '
    + ths.join(' / ') + ' 共 ' + ths.length + ' 个厚度档' + (MAT_NOTE_TAIL[one.material] || '')
})

// ==================== 数据源清单 ====================
// 每补充一个月对账单，在末尾追加一个文件名即可（顺序＝月份顺序）
export const HISTORY_SOURCE_FILES = [
  "2026-5-9 江山4月对账单.xlsx",
  "2026-5-9 江山5月对账单.xlsx",
  "江山2026年6月对账单.xlsx",
  "江山2026年7月对账单.xlsx",
  "江山2026年8月对账单.xlsx"
]

// 有效样本的日期范围（供面板标题展示）
export const HISTORY_DATE_RANGE = (() => {
  let min = ''
  let max = ''
  CLEAN_HISTORY.forEach(item => {
    if (!min || dayIndexOf(item[0]) < dayIndexOf(min)) min = item[0]
    if (!max || dayIndexOf(item[0]) > dayIndexOf(max)) max = item[0]
  })
  return { min: min, max: max }
})()

// ==================== 数据源汇总（数据驱动） ====================
// 面积按理论尺寸、重量按密度 7.85 t/m³（不锈钢 7.93）折算，与逐条吨价同口径
export const HISTORY_TOTAL = (() => {
  let area = 0
  let weight = 0
  let amount = 0
  CLEAN_HISTORY.forEach(item => {
    const sheetArea = item[4] * item[5]
    const matDensity = item[7] > 0 && String(item[1]).indexOf('不锈') >= 0 ? 7.93 : 7.85
    area += sheetArea
    weight += sheetArea * item[3] * matDensity / 1000
    amount += item[6]
  })
  return {
    area: Math.round(area * 100) / 100,
    weight: Math.round(weight * 1000) / 1000,
    amount: Math.round(amount),
    avgPerTon: weight ? Math.round(amount / weight) : 0
  }
})()

// 汇总口径写回模型配置（模板里 buyModel.source.* / buyModel.matNote 直接读）
PRICE_MODEL.source = Object.assign({}, PRICE_MODEL.source, {
  files: HISTORY_SOURCE_FILES,
  rawRecords: PRICE_HISTORY.length,
  dupRemoved: DUP_COUNT,
  records: CLEAN_HISTORY.length,
  area: HISTORY_TOTAL.area,
  weight: HISTORY_TOTAL.weight,
  amount: HISTORY_TOTAL.amount,
  avgPerTon: HISTORY_TOTAL.avgPerTon,
  range: HISTORY_DATE_RANGE.min + ' ~ ' + HISTORY_DATE_RANGE.max
})

// ==================== 加权算法（精确层与通用层共用） ====================
// 时间新度：0 = 该材质最早一条买料，1 = 最新一条
function recencyOf (item, extent) {
  const span = extent || DATE_EXTENT[item[1]]
  if (!span || span.max === span.min) return 1
  return (dayIndexOf(item[0]) - span.min) / (span.max - span.min)
}

// 时间权重倍率 = timeBoost ^ 新度（最新一条 = timeBoost 倍，最早一条 = 1 倍）
function timeFactorOf (item, extent) {
  const boost = PRICE_MODEL.timeBoost
  if (!boost || boost <= 1) return 1
  return Math.pow(boost, recencyOf(item, extent))
}

// 面积所属档位
export function areaBandOf (area) {
  const value = Number(area) || 0
  const bands = PRICE_MODEL.areaBand
  for (let i = 0; i < bands.length; i++) {
    if (value >= bands[i].min && value < bands[i].max) return bands[i]
  }
  return bands[bands.length - 1]
}

// 对一组成交记录做「面积接近度 × 时间新度」加权，返回吨价与逐条明细
//   options.extent  —— 时间新度归一化用的日期跨度（缺省取首条记录所属材质的跨度）
//   options.sortBy  —— 'weight'（默认，权重降序，预测口径）/ 'date'（日期倒序，浏览口径）
function weightedAverage (records, area, density, options) {
  const opts = options || {}
  const areaRef = area > 0 ? area : 1
  const extent = opts.extent || DATE_EXTENT[records[0][1]] || { min: 0, max: 0 }
  let num = 0
  let den = 0
  const rows = records.map(item => {
    const wArea = 1 / (Math.abs(item[4] - areaRef) + PRICE_MODEL.areaSmooth)
    const timeFactor = timeFactorOf(item, extent)
    const weight = wArea * timeFactor
    num += weight * item[7]
    den += weight
    return {
      date: item[0], mat: item[1], spec: item[2], thick: item[3],
      area: item[4], perTon: item[7], same: true,
      perSqm: item[7] * item[3] * density / 1000,
      timeFactor: timeFactor, recency: recencyOf(item, extent), latest: recencyOf(item, extent) >= 0.9999,
      daysAgo: Math.max(0, Math.round(extent.max - dayIndexOf(item[0]))),
      share: 0,
      weight: weight,
      row: item
    }
  })
  if (!den) return null
  if (opts.sortBy === 'date') {
    rows.sort((x, y) => dayIndexOf(y.date) - dayIndexOf(x.date))
  } else {
    rows.sort((x, y) => y.weight - x.weight)
  }
  rows.forEach(one => { one.share = one.weight / den * 100 })
  const byDate = records.slice().sort((x, y) => dayIndexOf(x[0]) - dayIndexOf(y[0]))
  return {
    perTon: num / den,
    rows: rows,
    poolCount: records.length,
    areaRef: areaRef,
    latestDate: byDate.length ? byDate[byDate.length - 1][0] : '',
    oldestDate: byDate.length ? byDate[0][0] : '',
    latestShare: rows.length ? rows[0].weight / den * 100 : 0
  }
}

// ==================== 取数（预测层与「历史成交记录」面板共用） ====================
// 一组记录的日期跨度（同材质时直接取该材质的完整跨度；跨材质时按这组记录自身算）
function extentOfRecords (records) {
  const mats = {}
  records.forEach(item => { mats[item[1]] = true })
  const keys = Object.keys(mats)
  if (keys.length === 1 && DATE_EXTENT[keys[0]]) return DATE_EXTENT[keys[0]]
  let min = Infinity
  let max = -Infinity
  records.forEach(item => {
    const day = dayIndexOf(item[0])
    if (day < min) min = day
    if (day > max) max = day
  })
  return { min: min, max: max }
}

// 解析本次要用的成交记录集
//   厚度 > 0 → ① 精确层（同材质 + 同厚度）；无记录则 ② 通用层（同材质样本最多档）
//   厚度 ≤ 0 → 不按厚度过滤：列出该材质的全部成交；该材质一条都没有则列出全部材质
function resolveHistory (material, thickness) {
  if (thickness > 0) {
    const exact = CLEAN_HISTORY.filter(item =>
      item[1] === material && Math.abs(item[3] - thickness) < 0.01)
    if (exact.length) return { scope: 'exact', records: exact, ref: null }
    const top = pickTopCombo(material, thickness)
    if (top) {
      return {
        scope: 'generic',
        records: top.records,
        ref: { material: top.material, thickness: top.thickness, count: top.records.length }
      }
    }
    return { scope: 'none', records: [], ref: null }
  }
  const own = material ? CLEAN_HISTORY.filter(item => item[1] === material) : []
  if (own.length) return { scope: 'all', records: own, ref: null }
  if (CLEAN_HISTORY.length) return { scope: 'allMat', records: CLEAN_HISTORY.slice(), ref: null }
  return { scope: 'none', records: [], ref: null }
}

/**
 * 「历史成交记录 · 预测依据」面板的取数（不填厚度时列出全部记录，供浏览核对）
 *
 * 与 predictSteelPrice 共用同一套取数逻辑，保证「面板列出的记录 = 预测实际用到的记录」。
 *
 * @returns {Object} { scope: 'exact'|'generic'|'all'|'allMat'|'none',
 *                     material, thickness, rows, poolCount, latestDate, oldestDate,
 *                     ref, matCount }
 */
export function historyRowsFor (options) {
  const opts = options || {}
  const material = modelMaterialOf(opts.material)
  const thickness = Number(opts.thickness) || 0
  const area = Number(opts.area) || 0
  const density = Number(opts.density) || 7.85

  const picked = resolveHistory(material, thickness)
  const empty = {
    scope: 'none', material: material, thickness: thickness,
    rows: [], poolCount: 0, latestDate: '', oldestDate: '', ref: null, matCount: 0
  }
  if (!picked.records.length) return empty

  const browsing = picked.scope === 'all' || picked.scope === 'allMat'
  const weighted = weightedAverage(picked.records, area, density, {
    extent: extentOfRecords(picked.records),
    sortBy: browsing ? 'date' : 'weight'
  })
  if (!weighted) return empty

  const mats = {}
  picked.records.forEach(item => { mats[item[1]] = true })
  return {
    scope: picked.scope,
    material: material,
    thickness: thickness,
    rows: weighted.rows,
    poolCount: picked.records.length,
    latestDate: weighted.latestDate,
    oldestDate: weighted.oldestDate,
    ref: picked.ref,
    matCount: Object.keys(mats).length
  }
}

// ==================== 对外主入口 ====================
/**
 * 预测钢材买料吨价
 *
 * @param {Object} options
 * @param {string} options.material  材质（可用报价单族 key，如 '304' / '铁板' / '锰板'）
 * @param {number} options.thickness 厚度 mm
 * @param {number} [options.area]    单件面积 ㎡（用于面积接近度加权，缺省按 1㎡ 处理）
 * @param {number} [options.density] 密度 t/m³（缺省 7.85）
 *
 * @returns {Object} {
 *   ok, level: 'exact' | 'generic' | 'none', levelLabel,
 *   material, requestMaterial, thickness,
 *   perTon, perSqm, isThin, poolCount, rows, latestDate, oldestDate,
 *   ref: null | { material, thickness, count, perTon },   // 通用层搬用的参考档
 *   note, formulaText
 * }
 */
export function predictSteelPrice (options) {
  const opts = options || {}
  const requestMaterial = opts.material == null ? '' : String(opts.material)
  const material = modelMaterialOf(requestMaterial)
  const thickness = Number(opts.thickness) || 0
  const area = Number(opts.area) || 0
  const density = Number(opts.density) || 7.85
  const thinThick = PRICE_MODEL.thinThick

  const result = {
    ok: false,
    level: 'none',
    levelLabel: '',
    material: material,
    requestMaterial: requestMaterial,
    thickness: thickness,
    perTon: 0,
    perSqm: 0,
    isThin: thickness > 0 && thickness <= thinThick,
    poolCount: 0,
    rows: [],
    latestDate: '',
    oldestDate: '',
    latestShare: 0,
    areaRef: area > 0 ? area : 1,
    density: density,
    ref: null,
    note: '',
    formulaText: ''
  }

  if (!(thickness > 0)) {
    result.note = '请输入厚度'
    result.formulaText = '请输入长、宽、厚，自动给出价格预测'
    return result
  }
  if (!material) {
    result.note = '未识别到材质，无法预测'
    result.formulaText = '请先选择材质'
    return result
  }

  const picked = resolveHistory(material, thickness)

  // ---------- ① 精确层：同材质 + 同厚度 ----------
  if (picked.scope === 'exact') {
    const weighted = weightedAverage(picked.records, area, density)
    if (weighted) {
      result.ok = true
      result.level = 'exact'
      result.levelLabel = '同材质同厚度成交加权'
      result.perTon = weighted.perTon
      result.perSqm = weighted.perTon * thickness * density / 1000
      result.poolCount = weighted.poolCount
      result.rows = weighted.rows
      result.latestDate = weighted.latestDate
      result.oldestDate = weighted.oldestDate
      result.latestShare = weighted.latestShare
      result.areaRef = weighted.areaRef
      result.formulaText = '预测吨价 = ' + material + ' ' + thickness + ' mm 的 ' + weighted.poolCount
        + ' 条成交记录按「面积接近度 × 时间新度」加权平均（只取同材质同厚度）'
        + '；成交日期越近权重越高，最新 ' + weighted.latestDate + ' 是最早 ' + weighted.oldestDate
        + ' 的 ' + PRICE_MODEL.timeBoost + ' 倍　=　' + Math.round(weighted.perTon) + ' 元/t'
      return result
    }
  }

  // ---------- ② 通用层：同材质内成交记录最多的厚度档 ----------
  if (picked.scope === 'generic') {
    const top = picked.ref
    const weighted = weightedAverage(picked.records, area, density)
    if (weighted) {
      result.ok = true
      result.level = 'generic'
      result.levelLabel = '通用预测 · 同材质样本最多档'
      result.perTon = weighted.perTon
      // 通用层的吨价搬自参考档，故平方价也必须按参考档厚度换算，不能用请求厚度（否则口径不一致）
      result.perSqm = weighted.perTon * top.thickness * density / 1000
      result.poolCount = weighted.poolCount
      result.rows = weighted.rows
      result.latestDate = weighted.latestDate
      result.oldestDate = weighted.oldestDate
      result.latestShare = weighted.latestShare
      result.areaRef = weighted.areaRef
      result.ref = {
        material: top.material,
        thickness: top.thickness,
        count: top.count,
        perTon: weighted.perTon
      }
      result.note = material + ' 无 ' + thickness + ' mm 成交记录，退回历史成交记录做通用预测：'
        + '搬用同材质样本最多的 ' + material + ' ' + top.thickness + ' mm（' + top.count
        + ' 条）加权吨价。仍在同材质内取数（不跨材质），但厚度不同，只作量级参考。'
      result.formulaText = '通用预测吨价 = ' + material + ' 无 ' + thickness + ' mm 成交记录，'
        + '退回历史成交记录，搬用同材质成交最多的 ' + material + ' ' + top.thickness + ' mm'
        + '（' + top.count + ' 条）加权平均　=　' + Math.round(weighted.perTon) + ' 元/t'
        + '（不跨材质；跨了厚度，只作量级参考）'
      return result
    }
  }

  // ---------- ③ 该材质在对账单中完全无记录 ----------
  result.note = '材质「' + material + '」在对账单中无成交记录，无法预测（'
    + (PRICE_MODEL.matNote[material] || '无历史成交价可参考') + '）'
  result.formulaText = result.note
  return result
}
